import {
    Component, ChangeDetectionStrategy, Input, ViewChild, ElementRef, OnInit, ViewChildren, QueryList, AfterViewInit
} from '@angular/core';
import { Observable, BehaviorSubject, combineLatest, Subscription} from 'rxjs';
import { Message } from 'src/typings/communication';
import { map, filter, first, delay, skip, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { DateUtilsService } from 'src/app/services/date-utils.service';
import { compareObjectArrays } from 'src/app/services/utils';
import { ScrollDispatcher, CdkScrollable } from '@angular/cdk/overlay';
import { maxBy } from 'lodash';
import { MessageDoc, FileWithType } from 'src/app/shared/sl-data/messages/messages-types';
import moment from 'moment';
import { Timestamp } from '@firebase/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { ref, getDownloadURL} from '@angular/fire/storage';
import { ModalServiceN } from 'src/app/shared/modal/modal.service';
import { MessageInputModalComponent } from '../../message-input-modal/message-input-modal.component';


interface MessageLog extends Message {
    type: 'message' | 'dates-separator';
    date: Date;
    id: string;
    cssClass: 'my-message' | '';
    showUserIcon: boolean;
    showTime: boolean;
    sent: boolean;
    read: boolean;
}

@Component({
    selector: 'app-chat-box',
    templateUrl: './chat-box.component.html',
    styleUrls: ['./chat-box.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatBoxComponent implements OnInit, AfterViewInit {
    private messages$ = new BehaviorSubject<MessageDoc[]>([]);
    private unsentMessages$ = new BehaviorSubject<MessageDoc[]>([]);
    public showMoreMessagesButton$ = new BehaviorSubject<boolean>(false);
    public liveDate$: Observable<Date>;
    private currentMessagesDaysLimit : number = 5;
    public textMessage:string = '';
    
    @ViewChild(CdkScrollable, { static: true }) private messagesContainer: CdkScrollable;
    @ViewChildren('dateSep') private dateSeparators: QueryList<ElementRef>;

    private showMoreMessagesButton = (msgs: MessageDoc[]) => {
        const messageDays = msgs.reduce((appDates, currentValue) => {
            const currentDate = currentValue.dateUtc.toDate().toISOString().split('T')[0].toString();    
            !appDates.includes(currentDate) && appDates.push(currentDate);
            return appDates;
        },[]);
        if(this.viewMoreMessagesCallback && this.currentMessagesDaysLimit === messageDays.length){
            this.showMoreMessagesButton$.next(true);
        }
    }

    private _softScroll = false;
    private _shouldScroll = false;
    private _id: string = '';
    @Input() set id(id: string) {//Use to detect a context change and update the component state.
        if(this._id !== id){
            this.textMessage = '';
            this.unsentMessages$.next([]);
            this._softScroll = false;
            this._shouldScroll = true;
        }
        this._id=id;
    } 


    @Input() otherRecipient : string;
    @Input() userName : string;
    @Input() displayDate : boolean = true;
    
    @Input() set messages(messages: MessageDoc[]) {
        // Skip update if messages are deeply equal to current value
        if (compareObjectArrays(this.messages$.value, messages || [])) {
            return;
        }
        this.messages$.next(messages || []);
        messages && this.showMoreMessagesButton(messages);
        if(this.updateAiFeedbackCallback && messages && messages.length > 0 && messages[messages.length - 1].aiSuggestedMessage){
            const lastMessage = messages[messages.length - 1];
            //Write AI suggested response if available
            if(!lastMessage.aiFeedback && this.textMessage === ''){//Only write AI suggestion when feedback was not sent and when user is not already typing
                this.aiFeedbackActive$.next(true);
                this.textMessage = lastMessage.aiSuggestedMessage;
            }
        }
        else{
            this.aiFeedbackActive$.next(false);
        }
    }

    public aiFeedbackActive$ = new BehaviorSubject<boolean>(false);

    public aiFeedbackClickHandler = (feedback: 'thumbs-up' | 'thumbs-down') => {
        const messages = this.messages$.value;
        if(messages?.length > 0 && messages[messages.length - 1].aiSuggestedMessage){
            //messages[messages.length - 1].aiFeedback = feedback;
            let feedbackExplanation = '';
            if(feedback === 'thumbs-down'){
                //Open a modal pop-up to request user explanation on what was wrong.
                this.modalService.show(MessageInputModalComponent, {
                    title: 'Why was this AI response not helpful?',
                    submitFn: (explanation: string) => {
                        this.aiFeedbackActive$.next(false);
                        feedbackExplanation = explanation;
                        if(this.textMessage === messages[messages.length - 1].aiSuggestedMessage){
                            this.textMessage = '';
                        }
                        this.updateAiFeedbackCallback(feedback, messages[messages.length - 1].id, feedbackExplanation);
                    },
                });
                return; // Don't call updateAiFeedbackCallback yet, wait for modal result
            }
            this.aiFeedbackActive$.next(false);
            this.updateAiFeedbackCallback(feedback, messages[messages.length - 1].id);
        }
    }

    public messageFiles$ = this.messages$.pipe(
        switchMap(async messages => await this.loadMessageFilesPublicUrls(messages))
    );

    public combinedMessages$ = combineLatest(this.messages$, this.unsentMessages$).pipe(
        map(([messages, unsentMessages]) => {
            const ago = moment().subtract(2, 'minutes').toDate();
            unsentMessages = unsentMessages.filter((unsentMessage) => !messages.some((sentMessage) => unsentMessage.message === sentMessage.message && sentMessage.dateUtc.toDate() > ago));
            //unsent messages where sent
            if(unsentMessages.length < this.unsentMessages$.value.length){
                this.unsentMessages$.next(unsentMessages);
            }
            return messages.concat(unsentMessages);
        })
    );
    
    @Input() viewMoreMessagesCallback : (limit : number) => void;

    @Input() addMessageCallback : (message : string) => Promise<void>;
    @Input() updateAiFeedbackCallback : (aiFeedback: 'thumbs-up' | 'thumbs-down', messageId: string, feedbackExplanation?: string) => Promise<void>;
    
    ngAfterViewInit(): void {
        if(!this.displayDate){
            this.liveDate$ = new BehaviorSubject(null);
        }
        else{
            this.liveDate$ = combineLatest(
                this.scroller.scrolled(10),
                this.dateSepElements$.pipe(
                    distinctUntilChanged((prev, cur) => cur.every(curEl => !!prev.find(prevEl => prevEl.id === curEl.id)))
                )
            ).pipe(
                map(([, separators]) => {
                    const headerHeight = 120;
                    return separators
                        .map(el => ({ el, fromTop: el.getBoundingClientRect().top - headerHeight }))
                        .filter(sep => sep.fromTop < 0);
                }),
                map(sepsAboveTop => {
                    const firstAboveTop = maxBy(sepsAboveTop, 'fromTop');
                    if (firstAboveTop) {
                        return new Date(firstAboveTop.el.id);
                    }
                })
            );
        }
    }

    private subscriptions: Subscription[] = [];
    ngOnInit(): void {
        this.subscriptions.push(this.messageLogs$.pipe(
            first(),
            delay(40),
        ).subscribe(() => this.scrollBottom()));

        this.subscriptions.push(this.messageLogs$.pipe(
            skip(1),
            delay(40),
        ).subscribe(() => this.scrollBottom()));
    }

    ngOnDestroy(): void {
        for(let subscription of this.subscriptions){
            subscription.unsubscribe();
        }
    }

    constructor(
        private dateUtils: DateUtilsService,
        private scroller: ScrollDispatcher,
        private storage: AngularFireStorage,
        private modalService: ModalServiceN
    ) {

    }

    private get dateSepElements$() {
        return this.dateSeparators.changes.pipe(
            map<QueryList<ElementRef>, HTMLElement[]>(dateSeps =>
                dateSeps.toArray().map((elRef: ElementRef) => elRef.nativeElement)
            )
        );
    }

    private sendMessage = async () => {
        const content = this.textMessage.trim();
        if (content !== '') {
            this.clearEditor();
            this.unsentMessages$.next(this.unsentMessages$.value.concat([{message: content, from: 1, dateUtc: Timestamp.now(), isUnread: true, unsent: true}]));
            await this.addMessageCallback(content);
        }
    }

    public sendMessageClickHandler = async () => {
        await this.sendMessage();
    }

    public viewMoreMessagesHandler = async () => {
        this.currentMessagesDaysLimit +=5;
        this.viewMoreMessagesCallback(this.currentMessagesDaysLimit);
    }
    
    public getUserName = (user: MessageDoc['user']) => {
        if(user.firstName && user.lastName){
            return `${user.firstName} ${user.lastName}`;
        }
        if(user.firstName){
            return user.firstName;
        }
        if(user.lastName){
            return user.lastName;
        }
        if(user.email){
            return user.email;
        }
        return 'Me';
    }

    public messageLogs$: Observable<MessageLog[]> = (
        this.combinedMessages$.pipe(
            filter(messages => !!messages),
            distinctUntilChanged((previousMessages, newMessages) => {
                const arraysAreEquals = compareObjectArrays(previousMessages, newMessages);
                if(previousMessages?.length !== newMessages?.length){
                    //Only scroll when there are new messages
                    //Don't set it to false if they are equals. It might have been set to true if it's a different job id
                    this._shouldScroll = true;
                }
                return arraysAreEquals;
            }),
            map(messages => messages
                .sort((msg1, msg2) =>
                    msg1.dateUtc.seconds - msg2.dateUtc.seconds
                )
                .map(msg => ({
                    ...msg,
                    cssClass: msg.from === 1 ? 'my-message' : '',
                    fromUser: msg.from && msg.user ? this.getUserName(msg.user) : '',
                    isAI: msg.from && msg.user && msg.user.isAI,
                    showUserIcon: msg.from !== 1,
                    sent: !msg.unsent,
                    read: msg.status === 'read' || !msg.isUnread,
                    delivered: msg.status === 'delivered',
                    images: msg.filesWithType ? msg.filesWithType.filter(fwt=>fwt.contentType.indexOf('image')>=0).map(fwt=>fwt.relativePath) : msg.files,
                    videos: msg.filesWithType ? msg.filesWithType.filter(fwt=>fwt.contentType.indexOf('video')>=0).map(fwt=>fwt.relativePath) : []
                }))
                .reduce(({ lastDisplayedDay: lastDay, lastDisplayedTime: lastMessageTime, messages }, msg, index, origArr) => {
                    const msgDate = msg.dateUtc.toDate();
                    const isNewDay = !this.dateUtils.isSameDay(msgDate, lastDay);
                    const sixtySecondsPassedSinceLast = !this.dateUtils.isWithinMinute(lastMessageTime, msgDate);
                    const previousMessage = origArr[index - 1];
                    const isSamePartyAsPrevious = previousMessage && previousMessage.from === msg.from;
                    const showTime = sixtySecondsPassedSinceLast || !isSamePartyAsPrevious;
                    return {
                        messages: [
                            ...messages,
                            ...(isNewDay ? [{
                                type: 'date-separator',
                                date: msgDate,
                                id: this.dateUtils.dateToISO(msgDate)
                            }] : []),
                            {
                                ...msg,
                                type: 'message',
                                date: msgDate,
                                showTime,
                            },
                        ],
                        lastDisplayedDay: isNewDay ? msgDate : lastDay,
                        lastDisplayedTime: showTime ? msgDate : lastMessageTime,

                    };
                }, { lastDisplayedDay: null, lastDisplayedTime: null, messages: [] }).messages
            )
        )
    );

    private clearEditor() {
        this.textMessage = '';
        this.aiFeedbackActive$.next(false);
    }

    public trackByFn(i, msg) {
        return i;
    }

    private scrollBottom() {
        let behavior: 'smooth' | 'auto' = 'smooth';
        if(!this._softScroll){
            this._softScroll = true;//set soft for next time
            behavior = 'auto';
        }
        if(this._shouldScroll){
            this.messagesContainer.scrollTo({ bottom: 0, behavior });
        }
        this._shouldScroll = false;
    }

    private loadMessageFilesPublicUrls = async (messages: MessageDoc[]) => {
        const images = [];
        for (let mIndex = 0; mIndex < (messages ?? []).length; mIndex++) {
            const message = messages[mIndex];
            const files: FileWithType[] = message.filesWithType ? message.filesWithType : (message.files?.map(relativePath=>{ return {relativePath,contentType:'image/jpg'} }) || []);
            for (let fIndex = 0; fIndex < files.length; fIndex++) {
                const fileWithType = files[fIndex];
                const imgRef = ref(this.storage.storage, fileWithType.relativePath);
                const url = await getDownloadURL(imgRef);
                images.push({relativePath: fileWithType.relativePath, publicPath: url, contentType: fileWithType.contentType});
            }
        }
        return images;
    }

    private mapMessageUrl(imageRelativePath, mappingToPublicUrl){
        if(mappingToPublicUrl){
            const publicPath = mappingToPublicUrl.find(m=>m.relativePath===imageRelativePath).publicPath;
            return publicPath;
        }
    }

    public currentImage$ = new BehaviorSubject('');

    public displayImage = (imageUrl) => {
        this.currentImage$.next(imageUrl);
    }

    public closeImage = () => {
        this.currentImage$.next('');
    }

    public onKeyPressed = async (event) => {
        if(event.keyCode===13){
            await this.sendMessage();
            event.stopPropagation();
            event.preventDefault();
            return false;
        }
        if( this.updateAiFeedbackCallback && 
            this.messages$?.value?.length > 0 && 
            this.messages$.value[this.messages$?.value.length - 1].aiSuggestedMessage && 
            !this.messages$.value[this.messages$?.value.length - 1].aiFeedback){
            const lastMessage = this.messages$?.value[this.messages$?.value.length - 1];
            //Write AI suggested response if available
            if(!lastMessage.aiFeedback && lastMessage.aiSuggestedMessage !== this.textMessage){
                this.aiFeedbackActive$.next(false);
                await this.updateAiFeedbackCallback('thumbs-down', lastMessage.id, 'Overwritten by user');
            }
        }
    }
    
}
